import { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { AppGnb } from 'components/ds';
import { isLoggedIn } from 'common/utils/auth';
import { getUserInfo } from 'common/utils/secureStorage';
import { pickNativeImage, isNativeApp, requestApp, WEB_TO_APP } from 'common/webview';
import config from 'config/config';
import { ChatRepository } from 'repository/chatRepository';
import styles from './ChatEmbed.module.css';

const CHAT_FRONT_URL = config.CHAT_FRONT_URL || '';

// 모임 화면·푸시는 모임 ID(MTG_…)로 채팅을 연다. wiz-chat 은 방 ID(ROOM-…)만 알아보므로
// (모임 ID 로 열면 403 ROOM_ACCESS_DENIED) 내 방 목록에서 그 모임의 방 ID 를 찾아 바꿔 연다.
const MEETING_CHAT_PATH = /^\/chat(?:\/room)?\/(MTG_[^/]+)\/?$/;

const getChatOrigin = (): string => {
    try { return new URL(CHAT_FRONT_URL).origin; } catch { return ''; }
};

export const ChatEmbed = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const chatOrigin = getChatOrigin();

    const getChatPath = () => {
        const path = location.pathname;
        return path.startsWith('/chat') ? path : '/chat';
    };

    const meetingIdInPath = location.pathname.match(MEETING_CHAT_PATH)?.[1];

    useEffect(() => {
        if (!meetingIdInPath) return;
        let cancelled = false;
        (async () => {
            let roomId: string | undefined;
            try {
                const res: any = await ChatRepository.onGetChatRoomList();
                const list = res?.list ?? res?.data?.list ?? [];
                roomId = (Array.isArray(list) ? list : []).find((r: any) => r?.meetingId === meetingIdInPath)?.roomId;
            } catch (e) {
                console.warn('[ChatEmbed] 모임 채팅방 찾기 실패', e);
            }
            if (cancelled) return;
            navigate(roomId ? `/chat/room/${roomId}` : '/chat', { replace: true });
        })();
        return () => { cancelled = true; };
    }, [meetingIdInPath, navigate]);

    // 토큰은 postMessage로만 전달 (URL 파라미터 사용 금지)
    const iframeSrc = `${CHAT_FRONT_URL}${getChatPath()}`;

    // iframe에 토큰 전송
    const sendTokenToIframe = useCallback(() => {
        const accessToken = localStorage.getItem('accessToken');
        const refreshToken = localStorage.getItem('refreshToken');
        if (accessToken && iframeRef.current?.contentWindow) {
            // DOKOCREW 의 저장된 유저정보에서 userNo/nickname 추출하여 함께 전달
            // (iframe 에서는 JWT 로 userNo 를 알 수 없음)
            const info = getUserInfo();
            iframeRef.current.contentWindow.postMessage({
                type: 'AUTH_TOKEN',
                accessToken,
                refreshToken,
                userNo: info?.userNo ? String(info.userNo) : '',
                nickname: info?.nickname || '',
            }, chatOrigin);
        }
    }, [chatOrigin]);

    // iframe에 응답 전송
    const replyToIframe = useCallback((requestId: string, type: string, payload: any) => {
        if (iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage({ type, requestId, payload }, chatOrigin);
        }
    }, [chatOrigin]);

    // parent 로그를 iframe 디버그 패널로 포워딩 (CHAT_LOG_PARENT 타입)
    const sendLogToIframe = useCallback((level: string, message: string) => {
        if (iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
                { type: 'PARENT_LOG', data: { level, message: `[parent] ${message}` } },
                chatOrigin
            );
        }
    }, [chatOrigin]);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // origin 검증 필수
            if (event.origin !== chatOrigin) return;

            const { type, requestId, data } = event.data || {};

            // 디버그: 수신한 모든 메시지 타입 찍기 (CHAT_LOG 제외 - 무한루프 방지)
            if (type && type !== 'CHAT_LOG') {
                sendLogToIframe('LOG', `msg received: type=${type}`);
            }

            switch (type) {
                case 'CHAT_LOG': {
                    // iframe(wiz-chat-front) 로그를 네이티브 로그로 포워딩
                    // dokocrew-front useWebview 의 console.log 오버라이드가 ReactNativeWebView.postMessage(LOGS) 로 전달
                    const msg = data?.message || '';
                    const lvl = data?.level || 'LOG';
                    if (lvl === 'ERR') console.error('[iframe]', msg);
                    else if (lvl === 'WRN') console.warn('[iframe]', msg);
                    else console.log('[iframe]', msg);
                    break;
                }
                case 'REQUEST_AUTH_TOKEN':
                    sendTokenToIframe();
                    break;

                case 'AUTH_EXPIRED':
                    if (isLoggedIn()) {
                        sendTokenToIframe();
                    } else {
                        navigate(`/login?returnUrl=${encodeURIComponent(location.pathname)}`);
                    }
                    break;

                case 'AUTH_REQUIRED':
                    if (!isLoggedIn()) {
                        navigate(`/login?returnUrl=${encodeURIComponent(location.pathname)}`);
                    } else {
                        sendTokenToIframe();
                    }
                    break;

                case 'PICK_IMAGE_REQUEST': {
                    // data.source: 'camera' | 'gallery'
                    const source: 'camera' | 'gallery' = data?.source === 'camera' ? 'camera' : 'gallery';
                    sendLogToIframe('LOG', `PICK_IMAGE_REQUEST received — source=${source} isNative=${isNativeApp()}`);

                    if (isNativeApp()) {
                        // 네이티브에 소스(camera/gallery) 힌트 전달
                        sendLogToIframe('LOG', `pickNativeImage(${source}) 호출 — requestId=${requestId}`);
                        pickNativeImage(source).then((img) => {
                            sendLogToIframe('LOG', `pickNativeImage resolved — hasImage=${!!img} hasUri=${!!img?.uri} hasBase64=${!!img?.base64} base64Len=${img?.base64?.length || 0}`);
                            replyToIframe(requestId, 'PICK_IMAGE_RESULT', img);
                            sendLogToIframe('LOG', `replyToIframe 호출 완료 — type=PICK_IMAGE_RESULT`);
                        });
                    } else {
                        // 웹 fallback: input[type=file] (capture 분기)
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        if (source === 'camera') input.setAttribute('capture', 'environment');
                        input.onchange = () => {
                            const file = input.files?.[0];
                            if (!file) {
                                replyToIframe(requestId, 'PICK_IMAGE_RESULT', null);
                                return;
                            }
                            const reader = new FileReader();
                            reader.onload = () => {
                                const dataUrl = reader.result as string;
                                const base64 = dataUrl.split(',')[1] || '';
                                replyToIframe(requestId, 'PICK_IMAGE_RESULT', {
                                    uri: dataUrl,
                                    base64,
                                    filename: file.name,
                                    mime: file.type || 'image/jpeg',
                                });
                            };
                            reader.onerror = () => replyToIframe(requestId, 'PICK_IMAGE_RESULT', null);
                            reader.readAsDataURL(file);
                        };
                        input.click();
                    }
                    break;
                }

                case 'CHAT_ROUTE_CHANGED': {
                    // iframe 내부 라우팅 변경 → parent URL 동기화 (replace, 히스토리 추가 없음)
                    const path = data?.path;
                    if (path && path !== location.pathname) {
                        window.history.replaceState(null, '', path);
                    }
                    break;
                }

                case 'DISMISS_CHAT_NOTIFICATIONS': {
                    // iframe 에서 요청 → 네이티브에 전달 (해당 roomId 의 쌓인 푸시 알림 제거)
                    if (isNativeApp()) {
                        requestApp(WEB_TO_APP.DISMISS_CHAT_NOTIFICATIONS, {
                            roomId: data?.roomId,
                        });
                    }
                    break;
                }

                case 'BLOCK_CHANGE': {
                    // iframe (wiz-chat-front) 에서 차단/해제 발생 → parent 의 인앱 알림 배너 동기화
                    const userNo = data?.userNo;
                    const kind = data?.kind; // 'block' | 'unblock'
                    if (userNo != null) {
                        window.dispatchEvent(new CustomEvent(
                            kind === 'unblock' ? 'dokocrew:unblockChange' : 'dokocrew:blockChange',
                            { detail: { userNo } },
                        ));
                    }
                    break;
                }

                case 'CHAT_READY': {
                    const pendingRaw = sessionStorage.getItem('_chatPendingLocation');
                    const msg1 = `CHAT_READY 수신 roomId=${data?.roomId} hasPending=${!!pendingRaw}`;
                    console.log('[ChatEmbed]', msg1);
                    sendLogToIframe('LOG', msg1);
                    if (!pendingRaw) break;
                    try {
                        const place = JSON.parse(pendingRaw);
                        if (iframeRef.current?.contentWindow) {
                            iframeRef.current.contentWindow.postMessage(
                                { type: 'SHARE_LOCATION', payload: place },
                                chatOrigin
                            );
                            const msg2 = `CHAT_READY → SHARE_LOCATION 전송 ${JSON.stringify(place)}`;
                            console.log('[ChatEmbed]', msg2);
                            sendLogToIframe('LOG', msg2);
                        }
                    } catch (e: any) {
                        const err = `pending location 파싱 실패: ${e?.message || e}`;
                        console.warn('[ChatEmbed]', err);
                        sendLogToIframe('WRN', err);
                    }
                    sessionStorage.removeItem('_chatPendingLocation');
                    sessionStorage.removeItem('_chatPlacePickerRoomId');
                    sessionStorage.removeItem('_chatPlacePickerReturnPath');
                    break;
                }

                case 'PICK_PLACE_REQUEST': {
                    const roomId = data?.roomId;
                    const chatPath = roomId ? `/chat/room/${roomId}` : '/chat';
                    const msg = `PICK_PLACE_REQUEST roomId=${roomId} chatPath=${chatPath}`;
                    console.log('[ChatEmbed]', msg);
                    sendLogToIframe('LOG', msg);
                    sessionStorage.setItem('_chatPlacePickerRoomId', roomId || '');
                    sessionStorage.setItem('_chatPlacePickerReturnPath', chatPath);
                    navigate('/meeting/place-search', {
                        state: {
                            returnTo: chatPath,
                            _chatPicker: true,
                        },
                    });
                    break;
                }
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [chatOrigin, sendTokenToIframe, navigate, location.pathname, replyToIframe]);

    // PlaceSearch 에서 복귀 → pending location 을 sessionStorage 에 저장 (iframe CHAT_READY 기다림)
    useEffect(() => {
        const pendingRoomId = sessionStorage.getItem('_chatPlacePickerRoomId');
        const m1 = `복귀 감지 pendingRoomId=${pendingRoomId} path=${location.pathname} hasState=${!!location.state} keys=${location.state ? Object.keys(location.state).join(',') : ''}`;
        console.log('[ChatEmbed]', m1);
        sendLogToIframe('LOG', m1);
        if (!pendingRoomId) return;

        const st: any = location.state;
        // PlaceSearch는 placeInfo 키로 저장. 하위호환을 위해 selectedPlaceInfo 도 확인
        const placeInfo = st?.placeInfo || st?.selectedPlaceInfo;
        if (!placeInfo || placeInfo.lat == null || placeInfo.lng == null) {
            const m2 = `placeInfo 없음 — 전송 건너뜀 state=${JSON.stringify(st)}`;
            console.log('[ChatEmbed]', m2);
            sendLogToIframe('WRN', m2);
            sessionStorage.removeItem('_chatPlacePickerRoomId');
            sessionStorage.removeItem('_chatPlacePickerReturnPath');
            return;
        }

        const place = {
            name: st.selectedPlace || placeInfo.name || '',
            address: placeInfo.address || '',
            lat: typeof placeInfo.lat === 'string' ? parseFloat(placeInfo.lat) : placeInfo.lat,
            lng: typeof placeInfo.lng === 'string' ? parseFloat(placeInfo.lng) : placeInfo.lng,
            imageUrl: placeInfo.imageUrl || '',
            rating: placeInfo.rating ?? null,
            reviewCount: placeInfo.reviewCount ?? null,
            categoryLabel: placeInfo.categoryLabel || '',
            source: placeInfo.source || 'kakao',  // 'kakao' | 'google'
        };

        sessionStorage.setItem('_chatPendingLocation', JSON.stringify(place));
        const m3 = `pending location 저장 — CHAT_READY 대기 중 ${JSON.stringify(place)}`;
        console.log('[ChatEmbed]', m3);
        sendLogToIframe('LOG', m3);

        navigate(location.pathname + location.search, { replace: true, state: null });
    }, [location.pathname, location.search, location.state, chatOrigin, navigate, sendLogToIframe]);

    return (
        <div className={styles.wrap}>
            <div className={styles.body}>
                {/* 모임 ID 경로는 방 ID 로 바꾼 뒤에 연다 — 그대로 열면 403 */}
                {!meetingIdInPath && <iframe
                    ref={iframeRef}
                    src={iframeSrc}
                    title={t('meeting.chatTitle')}
                    className={styles.frame}
                    allow="microphone; camera"
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                    onLoad={() => {
                        // iframe 내부 앱이 리스너를 등록할 시간 확보 후 토큰 전송
                        sendTokenToIframe();
                        setTimeout(sendTokenToIframe, 500);
                        setTimeout(sendTokenToIframe, 1500);
                    }}
                />}
            </div>
            <AppGnb />
        </div>
    );
};
